import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ImagePlus, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";

function normalizeIcon(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const icons = {};
      for (const size of [192, 512]) {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext("2d");
        if (!context) return reject(new Error("تعذر تجهيز صورة الأيقونة"));
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, size, size);
        const padding = Math.round(size * 0.085);
        const scale = Math.min((size - padding * 2) / image.width, (size - padding * 2) / image.height);
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        context.drawImage(image, Math.round((size - width) / 2), Math.round((size - height) / 2), width, height);
        icons[size] = canvas.toDataURL("image/png").split(",")[1];
      }
      resolve(icons);
    };
    image.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error("تعذر فتح الصورة")); };
    image.src = objectUrl;
  });
}

export default function StoreAppIconSettings({ companyId, companyName }) {
  const inputRef = useRef(null);
  const [iconUrl, setIconUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/store/settings")
      .then(({ data }) => { if (alive) setIconUrl(data?.app_icon_url || ""); })
      .catch(() => { if (alive) setIconUrl(""); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [companyId]);

  const upload = async file => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("اختر ملف صورة للشعار"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("حجم الصورة الأصلية يجب ألا يتجاوز 10MB"); return; }
    setUploading(true);
    try {
      const icons = await normalizeIcon(file);
      const { data } = await api.post("/store/app-icon", { content_type: "image/png", icons });
      setIconUrl(data.app_icon_url || "");
      toast.success("تم حفظ أيقونة تطبيق المتجر");
    } catch (error) {
      toast.error(formatErr(error) || "تعذر رفع أيقونة المتجر");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Card className="p-4 md:p-5" data-testid="store-app-icon-settings" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 shrink-0 rounded-2xl bg-primary/10 text-primary grid place-items-center"><Smartphone className="w-6 h-6" /></div>
        <div className="min-w-0 flex-1">
          <h2 className="font-black">تطبيق {companyName || "متجرك"} على الهاتف</h2>
          <p className="text-xs text-muted-foreground mt-1">ارفع شعارًا مربعًا؛ سيظهر مع اسم شركتك عند تثبيت هذا المتجر كتطبيق مستقل.</p>
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={event => upload(event.target.files?.[0])} />
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={loading || uploading} className="shrink-0">
          {uploading || loading ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <ImagePlus className="w-4 h-4 ml-2" />}
          {uploading ? "جارٍ رفع الشعار" : iconUrl ? "تغيير شعار التطبيق" : "رفع شعار التطبيق"}
        </Button>
        {iconUrl && <img src={iconUrl} alt={`شعار ${companyName || "المتجر"}`} className="w-14 h-14 rounded-xl border object-contain bg-white p-1" />}
      </div>
    </Card>
  );
}
